import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { HERO_IMAGE } from "../../data";
import { scrollToSection } from "./Nav";

const LINES = ["WEB", "DESIGNER"];

const STATS = [
  { value: "3", label: "Années d'expérience" },
  { value: "4", label: "Projets livrés" },
  { value: "100%", label: "Clients satisfaits" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const handleMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="accueil"
      ref={ref}
      className="relative min-h-screen flex items-center pt-[72px] overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.15)_0%,rgba(0,0,0,0)_70%)]" />

      <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 mb-8 flex items-center gap-2"
              data-testid="hero-eyebrow"
            >
              <MapPin size={14} /> Merdi — Québec, Canada
            </motion.p>

            <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl">
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                    className={`block ${i === LINES.length - 1 ? "bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 bg-clip-text text-transparent" : ""}`}
                    data-testid={`hero-line-${i}`}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed"
              data-testid="hero-description"
            >
              Je conçois et développe des expériences digitales sur-mesure où le design
              rencontre la performance. Chaque pixel compte, chaque interaction raconte une histoire.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <button
                onClick={() => scrollToSection("#projets")}
                className="group flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-300 shadow-[0_0_50px_-12px_rgba(59,130,246,0.5)]"
                data-testid="hero-cta-button"
              >
                Explorer mes travaux
                <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              <button
                onClick={() => scrollToSection("#contact")}
                className="text-zinc-300 hover:text-white font-mono text-sm uppercase tracking-[0.15em] border-b border-zinc-600 hover:border-blue-500 pb-1 transition-colors duration-300"
                data-testid="hero-secondary-cta"
              >
                Démarrer un projet
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.25 }}
              className="mt-16 grid grid-cols-3 gap-6 max-w-md"
              data-testid="hero-stats"
            >
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <p className="font-display font-bold text-2xl sm:text-3xl text-white">{stat.value}</p>
                  <p className="text-xs text-zinc-500 mt-1 font-mono uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-4 hidden lg:block" style={{ perspective: 1000 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              style={{ rotateX, rotateY, y: yImage, transformStyle: "preserve-3d" }}
              onMouseMove={handleMouse}
              onMouseLeave={() => {
                mx.set(0);
                my.set(0);
              }}
              className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]"
              data-testid="hero-image-card"
            >
              <img src={HERO_IMAGE} alt="Ondes abstraites néon" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080a]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                  Édition 2026
                </span>
                <span className="w-2 h-2 rounded-full bg-blue-500" />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
